import React, { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { TextData } from '../../../../types/blog';
import { MarkdownToolbar } from '../MarkdownToolbar';

interface Props {
  data: TextData;
  onChange: (data: TextData) => void;
}

export const TextSectionEditor: React.FC<Props> = ({ data, onChange }) => {
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Accept both `content_md` (editor-native) and `body` (third-party API payloads).
  // Always write back as `content_md` so the shape is normalised after first edit.
  const value = data.content_md ?? (data as unknown as { body?: string }).body ?? '';
  const handleChange = (v: string) => onChange({ content_md: v });

  return (
    <div className="flex flex-col gap-0">
      {/* Tab row */}
      <div className="flex justify-end gap-1 mb-1">
        {(['edit', 'preview'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className="ds-btn ds-btn-ghost min-h-[44px] min-w-[44px] px-4 touch-manipulation"
            style={tab === t ? { background: 'var(--ds-brand)', color: 'var(--ds-text-on-brand)', borderColor: 'var(--ds-brand)' } : undefined}
          >
            {t === 'edit' ? 'Markdown' : 'Preview'}
          </button>
        ))}
      </div>

      {tab === 'edit' ? (
        <>
          <MarkdownToolbar
            textareaRef={textareaRef}
            value={value}
            onChange={handleChange}
          />
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            rows={8}
            placeholder="Write content in **markdown**…  Use the toolbar to insert links."
            className="ds-textarea rounded-t-none font-mono"
          />
        </>
      ) : (
        <div className="ds-card-sm min-h-[8rem] prose prose-sm max-w-none dark:prose-invert">
          {value ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          ) : (
            <span className="t-meta italic">Nothing to preview.</span>
          )}
        </div>
      )}
    </div>
  );
};
